import { SiFramer } from 'react-icons/si'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import type { FramerWebPublisherArgs } from 'sprites-types/workflow/tools/framerWebPublisher'
import { framerWebPublisher } from 'sprites-types/workflow/tools/framerWebPublisher'
import { WebPublisherResult } from 'sprites-types/workflow/tools/webPublisher'
import { BaseWebPublisherDisplay } from '../shared/BaseWebPublisherDisplay'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'
import { FramerCMSItemDisplay } from './FramerCMSItemDisplay'
import { FramerCollectionDisplay } from './FramerCollectionDisplay'
import { FramerObjectDisplay, isJsonString, safeJsonParse } from './FramerObjectDisplay'

const getActionDisplayName = (action: string) => {
  switch (action) {
    case 'create':
      return 'Create CMS Item'
    case 'update':
      return 'Update CMS Item'
    case 'subagentTaskRunner':
      return 'Natural Language Task'
    case 'getContent':
      return 'Get Content'
    case 'listCollections':
      return 'List Collections'
    case 'getPublishUrls':
      return 'Get Publish URLs'
    default:
      return action
  }
}

function FramerWebPublisherDisplayComponent({ nodeExecution }: WorkflowToolComponentProps) {
  const inputs = framerWebPublisher.argsSchema.safeParse(nodeExecution.inputs.functionArgs)

  if (!inputs.success) {
    return <SchemaValidationError error={inputs.error} nodeExecution={nodeExecution} />
  }

  const schema = getToolResultSchema(framerWebPublisher, inputs.data)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const data = parseResult.data as WebPublisherResult
  const inputData = inputs.data as FramerWebPublisherArgs
  const action = inputData.action
  const actionDisplayName = getActionDisplayName(action)

  return (
    <BaseWebPublisherDisplay
      data={data}
      platformName="Framer"
      platformIcon={<SiFramer className="h-4 w-4 text-[#0099ff]" />}
      platformColor="text-[#0099ff]"
      backgroundColor="bg-blue-50"
      actionName={actionDisplayName}
    >
      {/* Action-specific additional content */}
      <ActionSpecificContent action={action} data={data} />
    </BaseWebPublisherDisplay>
  )
}

interface ActionSpecificContentProps {
  action: string
  data: WebPublisherResult
}

/**
 * Renders action-specific content for certain Framer operations
 */
function ActionSpecificContent({ action, data }: ActionSpecificContentProps) {
  // For certain actions, we might want to show additional structured information
  // beyond what's in the changes array

  switch (action) {
    case 'listCollections':
      // Try to find collections data in the changes details
      const collectionsChange = data.changes?.find(
        (change) =>
          change.action.toLowerCase().includes('list') ||
          change.target.toLowerCase().includes('collection')
      )

      if (collectionsChange && isJsonString(collectionsChange.details)) {
        const collections = safeJsonParse(collectionsChange.details)
        if (Array.isArray(collections) && collections.length > 0 && collections[0]?.name) {
          return (
            <div className="mt-3">
              <FramerCollectionDisplay
                collections={collections}
                title="Available Collections"
                showDetails={true}
              />
            </div>
          )
        }
      }
      break

    case 'getContent':
      // Try to find CMS item data in the changes details
      const contentChange = data.changes?.find(
        (change) =>
          change.action.toLowerCase().includes('retrieve') ||
          change.target.toLowerCase().includes('item')
      )

      if (contentChange && isJsonString(contentChange.details)) {
        const item = safeJsonParse(contentChange.details)
        if (item?.fieldData) {
          return (
            <div className="mt-3">
              <FramerCMSItemDisplay items={[item]} title="Retrieved Content" showFullData={true} />
            </div>
          )
        }
      }
      break

    case 'getPublishUrls':
      // Try to find publish URLs data
      const urlsChange = data.changes?.find(
        (change) =>
          change.action.toLowerCase().includes('retrieve') ||
          change.target.toLowerCase().includes('url')
      )

      if (urlsChange && isJsonString(urlsChange.details)) {
        const urls = safeJsonParse(urlsChange.details)
        if (urls && (urls.productionUrl || urls.stagingUrl)) {
          return (
            <div className="mt-3">
              <FramerObjectDisplay
                title="Publish URLs"
                data={urls}
                defaultExpanded={true}
                showExpandButton={false}
              />
            </div>
          )
        }
      }
      break
  }

  return null
}

async function downloadFramerWebPublisher(_nodeExecution: NodeExecution): Promise<void> {
  // TODO: Implement download functionality
  console.log('Download not implemented yet for Framer Web Publisher')
}

function framerWebPublisherToMarkdown(nodeExecution: NodeExecution): string {
  const inputs = framerWebPublisher.argsSchema.safeParse(nodeExecution.inputs.functionArgs)

  if (!inputs.success) {
    return `## Framer Web Publisher Error\n\nFailed to parse inputs: ${inputs.error.message}`
  }

  const schema = getToolResultSchema(framerWebPublisher, inputs.data)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return `## Framer Web Publisher Error\n\nFailed to parse outputs: ${parseResult.error.message}`
  }

  const data = parseResult.data as WebPublisherResult
  const inputData = inputs.data as FramerWebPublisherArgs
  const action = inputData.action
  const actionDisplayName = getActionDisplayName(action)

  return `## Framer ${actionDisplayName}

**Action:** ${action}

### Results

${data.summary}

${data.url ? `**URL:** ${data.url}\n` : ''}
${
  data.changes && data.changes.length > 0
    ? `### Changes Made

${data.changes.map((change) => `- **${change.action}** ${change.target}: ${change.details}`).join('\n')}
`
    : ''
}
---
`
}

export const FramerWebPublisherDisplay = createWorkflowToolComponent(
  FramerWebPublisherDisplayComponent,
  downloadFramerWebPublisher,
  framerWebPublisherToMarkdown
)
